import React, { useState } from 'react';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    Form,
    FormField,
    Input,
    Button,
    Message,
    Header,
    Icon,
    Segment,
    Table,
} from 'semantic-ui-react';
import { LoadingWrapper, Unauthorized } from '../common';
import { useAuth } from '../auth';
import { useFormChecklistForm } from './hooks';
import './FormChecklist.css';

// ─── Drag overlay cards ───────────────────────────────────────────────────────

const StepOverlayCard = ({ step }) => (
    <div className="fc-drag-card fc-drag-card-step">
        <Icon name="bars" />
        <strong>{step?.name || 'Step'}</strong>
    </div>
);

const TaskOverlayCard = ({ task }) => (
    <div className="fc-drag-card fc-drag-card-task">
        <Icon name="bars" />
        <span>{task?.description || 'Task'}</span>
    </div>
);

// ─── SortableTaskRow ──────────────────────────────────────────────────────────

const SortableTaskRow = ({ task, taskIdx, stepIdx, updateTask, removeTask }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: task._uid });

    return (
        <tr ref={setNodeRef} style={{ opacity: isDragging ? 0 : 1 }}>
            <td className="collapsing">
                <span className="fc-drag-handle" {...attributes} {...listeners} title="Drag to reorder">
                    <Icon name="bars" fitted />
                </span>
                <span className="fc-order-num">{stepIdx + 1}.{taskIdx + 1}</span>
            </td>
            <td>
                <Form.TextArea
                    className="fc-task-textarea"
                    fluid
                    value={task.description}
                    onChange={e => updateTask(stepIdx, taskIdx, 'description', e.target.value)}
                    placeholder="Task description"
                />
            </td>
            <td className="center aligned">
                <Button icon color="red" size="tiny" onClick={() => removeTask(stepIdx, taskIdx)} title="Remove task">
                    <Icon name="minus" />
                </Button>
            </td>
        </tr>
    );
};

// ─── SortableStepRow ─────────────────────────────────────────────────────────

const SortableStepRow = ({
    step, stepIdx, updateStep, removeStep, addTask, removeTask, updateTask, fieldErrors,
}) => {
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: step._uid });

    return (
        <>
            <tr
                ref={setNodeRef}
                className="fc-step-row"
                style={{ opacity: isDragging ? 0 : 1 }}
            >
                <td className="collapsing">
                    <span className="fc-drag-handle" {...attributes} {...listeners} title="Drag to reorder step">
                        <Icon name="bars" fitted />
                    </span>
                    <strong className="fc-order-num">{stepIdx + 1}</strong>
                </td>
                <td>
                    <Input
                        fluid
                        value={step.name}
                        onChange={e => updateStep(stepIdx, 'name', e.target.value)}
                        placeholder="Step name"
                    />
                </td>
                <td className="center aligned">
                    <Button icon color="red" size="small" onClick={() => removeStep(stepIdx)} title="Remove step">
                        <Icon name="minus circle" />
                    </Button>
                </td>
            </tr>

            {fieldErrors[`step_${stepIdx}_tasks`] && (
                <Table.Row>
                    <Table.Cell colSpan={3}>
                        <Message negative size="small" content={fieldErrors[`step_${stepIdx}_tasks`][0]} />
                    </Table.Cell>
                </Table.Row>
            )}

            {!isDragging && (
                <SortableContext items={step.tasks.map(t => t._uid)} strategy={verticalListSortingStrategy}>
                    {step.tasks.map((task, taskIdx) => (
                        <SortableTaskRow
                            key={task._uid}
                            task={task}
                            taskIdx={taskIdx}
                            stepIdx={stepIdx}
                            updateTask={updateTask}
                            removeTask={removeTask}
                        />
                    ))}
                </SortableContext>
            )}

            {!isDragging && (
                <Table.Row>
                    <Table.Cell colSpan={3}>
                        <Button size="tiny" onClick={() => addTask(stepIdx)}>
                            <Icon name="plus" />
                            Add Task
                        </Button>
                    </Table.Cell>
                </Table.Row>
            )}
        </>
    );
};

// ─── FormChecklistForm ────────────────────────────────────────────────────────

export const FormChecklistForm = () => {
    const { userIsIccSupportTeam, isReadOnly } = useAuth();
    const {
        isEditMode,
        fields,
        steps,
        loaded,
        saving,
        fieldErrors,
        submitError,
        handleFieldChange,
        addStep,
        removeStep,
        updateStep,
        reorderSteps,
        addTask,
        removeTask,
        updateTask,
        reorderTasks,
        handleSubmit,
    } = useFormChecklistForm({ userIsIccSupportTeam });

    const [activeId, setActiveId] = useState(null);
    const activeStep = steps.find(s => s._uid === activeId);
    const activeTask = !activeStep ? steps.flatMap(s => s.tasks).find(t => t._uid === activeId) : null;

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragStart = ({ active }) => setActiveId(active.id);
    const handleDragEnd = ({ active, over }) => {
        setActiveId(null);
        if (!over || active.id === over.id) return;
        if (activeStep) {
            const fromIdx = steps.findIndex(s => s._uid === active.id);
            const toIdx = steps.findIndex(s => s._uid === over.id);
            if (fromIdx !== -1 && toIdx !== -1) reorderSteps(fromIdx, toIdx);
        } else {
            let fromStepIdx = -1, toStepIdx = -1;
            for (let i = 0; i < steps.length; i++) {
                if (steps[i].tasks.some(t => t._uid === active.id)) fromStepIdx = i;
                if (steps[i].tasks.some(t => t._uid === over.id)) toStepIdx = i;
            }
            if (fromStepIdx !== -1 && fromStepIdx === toStepIdx) {
                const fromTaskIdx = steps[fromStepIdx].tasks.findIndex(t => t._uid === active.id);
                const toTaskIdx = steps[toStepIdx].tasks.findIndex(t => t._uid === over.id);
                if (fromTaskIdx !== -1 && toTaskIdx !== -1) reorderTasks(fromStepIdx, fromTaskIdx, toTaskIdx);
            }
        }
    };

    if (!userIsIccSupportTeam || isReadOnly) return <Unauthorized />;
    if (isEditMode && !loaded) return <LoadingWrapper loading />;

    return (
        <div className="fc-form-container">
            <Header as="h2">
                {isEditMode ? 'Edit Checklist' : 'New Checklist'}
            </Header>

            {submitError && <Message negative content={submitError} />}

            <Segment>
                <Header as="h4">Checklist Details</Header>
                <Form>
                    <FormField error={!!fieldErrors['name']}>
                        <label>Name</label>
                        <Input
                            value={fields.name}
                            onChange={e => handleFieldChange('name', e.target.value)}
                            placeholder="Checklist name"
                        />
                        {fieldErrors['name'] && (
                            <span className="fc-field-error-text">{fieldErrors['name'][0]}</span>
                        )}
                    </FormField>
                    <FormField error={!!fieldErrors['activity']}>
                        <label>Activity</label>
                        <Form.TextArea
                            value={fields.activity}
                            onChange={e => handleFieldChange('activity', e.target.value)}
                            placeholder="e.g. Splicing"
                            rows={2}
                        />
                        {fieldErrors['activity'] && (
                            <span className="fc-field-error-text">{fieldErrors['activity'][0]}</span>
                        )}
                    </FormField>
                    <FormField error={!!fieldErrors['condition']}>
                        <label>Condition</label>
                        <Form.TextArea
                            value={fields.condition}
                            onChange={e => handleFieldChange('condition', e.target.value)}
                            placeholder="e.g. Aerial"
                            rows={2}
                        />
                        {fieldErrors['condition'] && (
                            <span className="fc-field-error-text">{fieldErrors['condition'][0]}</span>
                        )}
                    </FormField>
                    <FormField error={!!fieldErrors['standard']}>
                        <label>Standard</label>
                        <Form.TextArea
                            value={fields.standard}
                            onChange={e => handleFieldChange('standard', e.target.value)}
                            placeholder="e.g. TEO"
                            rows={2}
                        />
                        {fieldErrors['standard'] && (
                            <span className="fc-field-error-text">{fieldErrors['standard'][0]}</span>
                        )}
                    </FormField>
                </Form>
            </Segment>

            {fieldErrors['steps'] && <Message negative content={fieldErrors['steps'][0]} />}

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={steps.map(s => s._uid)} strategy={verticalListSortingStrategy}>
                    <Table celled structured unstackable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell colSpan={3} className="fc-table-main-header">
                                    Add and Arrange Steps and their Tasks
                                </Table.HeaderCell>
                            </Table.Row>
                            <Table.Row>
                                <Table.HeaderCell collapsing></Table.HeaderCell>
                                <Table.HeaderCell>Step / Task</Table.HeaderCell>
                                <Table.HeaderCell collapsing textAlign="center">Actions</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {steps.length === 0 ? (
                                <Table.Row>
                                    <Table.Cell colSpan={3} textAlign="center" className="fc-empty-steps">
                                        <Button
                                            style={{ margin: '0.5rem' }}
                                            size="tiny"
                                            onClick={() => addStep(0)}
                                            className="fc-insert-step-btn"
                                        >
                                            <Icon name="plus" />
                                            Add Step
                                        </Button>
                                    </Table.Cell>
                                </Table.Row>
                            ) : (
                                steps.map((step, stepIdx) => (
                                    <React.Fragment key={step._uid}>
                                        <SortableStepRow
                                            step={step}
                                            stepIdx={stepIdx}
                                            updateStep={updateStep}
                                            removeStep={removeStep}
                                            addTask={addTask}
                                            removeTask={removeTask}
                                            updateTask={updateTask}
                                            fieldErrors={fieldErrors}
                                        />
                                        <Table.Row className="fc-insert-row">
                                            <Table.Cell colSpan={3} textAlign="center">
                                                <Button
                                                    style={{ margin: '0.5rem' }}
                                                    size="tiny"
                                                    onClick={() => addStep(stepIdx + 1)}
                                                >
                                                    <Icon name="plus" />
                                                    Add Step
                                                </Button>
                                            </Table.Cell>
                                        </Table.Row>
                                    </React.Fragment>
                                ))
                            )}
                        </Table.Body>
                    </Table>
                </SortableContext>
                <DragOverlay>
                    {activeStep && <StepOverlayCard step={activeStep} />}
                    {activeTask && <TaskOverlayCard task={activeTask} />}
                </DragOverlay>
            </DndContext>

            <div className="fc-actions-bar">
                <Button primary loading={saving} onClick={handleSubmit}>
                    <Icon name="save" />
                    Save Checklist
                </Button>
            </div>
        </div>
    );
};
